// Live model prices from LiteLLM's public table (raw.githubusercontent.com: no key, CORS "*", updated by the
// community within days of a launch). Its ids are the providers' own ("gpt-6-astra", "claude-sonnet-5",
// "gemini/gemini-3.8-flash") and it carries the per-query web-search fee, so a model the user just picked in
// Settings gets a price without a release of this app. Trimmed to the providers we call and cached a day in
// localStorage; the hand-written table in cost.ts is the fallback while this is loading or unreachable.
import { useSyncExternalStore } from 'react';
import type { Provider } from '@/types';

const FEED_URL = 'https://raw.githubusercontent.com/BerriAI/litellm/main/model_prices_and_context_window.json';
const STORAGE_KEY = 'assetlist.priceFeed.v1';
const TTL_MS = 24 * 60 * 60 * 1000;
// Providers whose rows are kept: the three we call, plus their resellers as a same-name approximation.
const KEEP = new Set(['openai', 'anthropic', 'gemini', 'azure', 'vertex_ai-language-models']);

export interface FeedPrice {
  input: number; // USD per 1M tokens
  output: number;
  search?: number; // USD per web-search query, when the table knows it
}

export interface PriceFeed {
  fetchedAt: number;
  rows: Record<string, FeedPrice>;
}

interface RawRow {
  litellm_provider?: string;
  mode?: string;
  input_cost_per_token?: number;
  output_cost_per_token?: number;
  search_context_cost_per_query?: { search_context_size_medium?: number; search_context_size_low?: number } | null;
}

/** Pure; exported for tests. Keeps chat models of the providers we care about, in USD per million tokens. */
export function parseFeed(json: Record<string, unknown>): Record<string, FeedPrice> {
  const rows: Record<string, FeedPrice> = {};
  for (const [id, raw] of Object.entries(json)) {
    const r = raw as RawRow;
    if (!r || typeof r !== 'object' || !KEEP.has(r.litellm_provider ?? '') || (r.mode && r.mode !== 'chat')) continue;
    if (typeof r.input_cost_per_token !== 'number' || typeof r.output_cost_per_token !== 'number') continue;
    const search = r.search_context_cost_per_query?.search_context_size_medium ?? r.search_context_cost_per_query?.search_context_size_low;
    const perMillion = (v: number) => Math.round(v * 1e6 * 1e6) / 1e6; // per-token floats × 1e6 leave noise like 0.09999999
    rows[id] = { input: perMillion(r.input_cost_per_token), output: perMillion(r.output_cost_per_token), ...(typeof search === 'number' ? { search } : {}) };
  }
  return rows;
}

const stripDate = (model: string) => model.replace(/[-@]?\d{4}-?\d{2}-?\d{2}$/, '');

/**
 * Pure; exported for tests. The provider's own id first (with any date suffix dropped), then the same model
 * name under a reseller (azure/…, vertex …) as an approximation.
 */
export function lookupFeed(feed: PriceFeed | null, provider: Provider, model: string): { price: FeedPrice; approx: boolean } | null {
  if (!feed) return null;
  const m = model.trim();
  const names = Array.from(new Set([m, stripDate(m)]));
  const exact = provider === 'gemini' ? names.flatMap((n) => [`gemini/${n}`, n]) : names;
  for (const key of exact) if (feed.rows[key]) return { price: feed.rows[key], approx: false };
  for (const n of names) {
    const hit = Object.keys(feed.rows).find((k) => k.endsWith(`/${n}`));
    if (hit) return { price: feed.rows[hit], approx: true };
  }
  return null;
}

let feed: PriceFeed | null = null;
let loaded = false;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function loadCached(): void {
  if (loaded) return;
  loaded = true;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw) as PriceFeed;
      if (parsed && typeof parsed.fetchedAt === 'number' && parsed.rows) feed = parsed;
    }
  } catch {
    feed = null;
  }
}

export function priceFeed(): PriceFeed | null {
  loadCached();
  return feed;
}

export function subscribePriceFeed(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Re-renders when the feed lands, so an estimate shown before it loaded gets the live price. */
export function usePriceFeed(): PriceFeed | null {
  return useSyncExternalStore(subscribePriceFeed, priceFeed, priceFeed);
}

/** Fetches once a day, or now when forced (the model list was just refreshed). Never throws. */
export function refreshPriceFeed(force = false): Promise<void> {
  loadCached();
  if (!force && feed && Date.now() - feed.fetchedAt < TTL_MS) return Promise.resolve();
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetch(FEED_URL, { cache: 'no-store' });
      if (!res.ok) return;
      const rows = parseFeed((await res.json()) as Record<string, unknown>);
      if (Object.keys(rows).length === 0) return;
      feed = { fetchedAt: Date.now(), rows };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(feed));
      } catch {
        // storage full or unavailable: the in-memory copy still serves this session
      }
      emit();
    } catch {
      // offline: keep whatever we had
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}
